import React, { useEffect, useState } from 'react';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { XCircleFill, Download } from 'react-bootstrap-icons';
import { useDataGeneration, XMLLibraryEntry } from '../api/generate/store';
import { getLibraryFileHeader } from '../api/generate/actions';
import useUxStore from '../api/ux/store';
import { useFilenameConfigDialog } from '../components/FilenameConfigDialog';
import CStack from '../components/CStack';
import BaseControl from '../components/BaseControl';
import Label from '../components/Label';
import { buildProoflistFilename } from '../utils/functions';

const HistoryContainer = () => {
    const {
        libraryFiles,
        isLoadingLibrary,
        fetchLibraryFiles,
        loadLibraryFile,
        isLoadingFile,
        error,
        deleteLibraryFile,
        isDeleting,
        downloadGenerated,
    } = useDataGeneration(state => state);
    const { refreshDialog, addToast } = useUxStore(state => state);
    const { showFilenameDialog } = useFilenameConfigDialog();

    const [hoveredItemKey, setHoveredItemKey] = useState<string | null>(null);
    const [submissionType, setSubmissionType] = useState<number>(0);

    useEffect(() => {
        fetchLibraryFiles();
    }, [fetchLibraryFiles]);

    const handleFileClick = async (filename: string, cutoff: string) => {
        if (isLoadingFile || isDeleting) return;
        await loadLibraryFile(filename, cutoff);
    };

    const handleDownload = async (event: React.MouseEvent, filename: string) => {
        event.stopPropagation();
        
        let fileHeader: any = null;
        
        try {
            fileHeader = await getLibraryFileHeader(filename);
        } catch (err) {
            console.error("Failed to fetch library file header:", err);
        }
        
        const providerCode = fileHeader?.["-ProviderCode"] || '';
        const reportPeriod = fileHeader?.["-FileReferenceDate"] || '';
        
        showFilenameDialog(
            ({ filename: cocreeFilename, submissionType: selectedType, submissionAttempt: dialogSubmissionAttempt }) => {
                setSubmissionType(selectedType);
                const prooflistName = buildProoflistFilename(fileHeader ?? {}, dialogSubmissionAttempt, selectedType);
                const p = performLibraryDownload(filename, cocreeFilename, selectedType);
                p.finally(() => {
                    performLibraryDownloadProoflist(prooflistName, filename, cocreeFilename, selectedType);
                });
            },
            { 
                initialSubmissionType: submissionType,
                providerCode,
                reportPeriod,
            }
        );
    };
    
    const performLibraryDownload = async (originalFilename: string, downloadFilename: string, selectedType: number) => {
        try {
            const result = await downloadGenerated(false, downloadFilename, originalFilename, undefined, selectedType, undefined, downloadFilename);
            if (result.code === "200") {
                addToast("Download Complete", `Library file downloaded as: ${downloadFilename}`, null, "success");
            } else {
                addToast("Download Failed", "Please check your connection and try again.", null, "danger");
            }
        } catch (error) {
            console.error("Download error:", error);
            addToast("Download Error", "An unexpected error occurred during download.", null, "danger");
        }
    };
    
    const performLibraryDownloadProoflist = async (prooflistName: string, originalFilename: string, cocreeFilename: string, selectedType: number) => {
        try {
            const result = await downloadGenerated(true, prooflistName, originalFilename, "prooflist", selectedType, undefined, cocreeFilename);
            if (result.code === "200") {
                addToast("Prooflist Downloaded", `Prooflist file downloaded as: ${prooflistName}`, null, "success");
            }
        } catch (error) {
            console.error("Prooflist download error:", error);
        }
    };

    const handleDelete = (event: React.MouseEvent, fileKey: string, filename: string) => {
        event.stopPropagation();

        const dialogId = `delete-confirm-${filename}`;

        refreshDialog({
            id: dialogId,
            title: "Confirm Deletion",
            content: `Are you sure you want to delete the history item "${filename}"? This action cannot be undone.`,
            confirmContent: "Delete",
            confirmActive: true,
            visible: true,
            onConfirm: () => {
                console.log(`Confirmed deletion for: ${filename}`);
                deleteLibraryFile(filename)
                    .then(resultCode => {
                        if (resultCode === "200" || resultCode === "204") {
                            console.log(`Successfully deleted ${filename}. Store should have refreshed the list.`);
                            addToast("", "Deletion success", null, "success")
                        } else {
                            console.error(`Store action failed to delete ${filename}. Code: ${resultCode}`);
                            refreshDialog({
                                id: `delete-error-${filename}`,
                                title: "Deletion Failed",
                                content: `Could not delete "${filename}". Please check console or try again. (Code: ${resultCode})`,
                                visible: true,
                                confirmContent: "OK",
                                onConfirm: () => { }
                            }, true);
                        }
                    })
                    .catch(err => {
                        console.error(`Error during deleteLibraryFile call for ${filename}:`, err);
                        refreshDialog({
                            id: `delete-exception-${filename}`,
                            title: "Deletion Error",
                            content: `An unexpected error occurred while trying to delete "${filename}". Error: ${err.message || 'Unknown error'}`,
                            visible: true,
                            confirmContent: "OK",
                            onConfirm: () => { }
                        }, true);
                    });
            },
        }, true);
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
            
            // Less than 1 minute
            if (diffInSeconds < 60) {
                return diffInSeconds <= 1 ? "Just now" : `${diffInSeconds} seconds ago`;
            }
            
            // Less than 1 hour
            const diffInMinutes = Math.floor(diffInSeconds / 60);
            if (diffInMinutes < 60) {
                return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
            }
            
            // Less than 24 hours
            const diffInHours = Math.floor(diffInMinutes / 60);
            if (diffInHours < 24) {
                return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
            }
            
            // Less than or equal to 3 days
            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays === 1) {
                return "Yesterday";
            }
            if (diffInDays <= 3) {
                return `${diffInDays} days ago`;
            }
            
            // More than 3 days - show actual date and time
            return date.toLocaleString();
            
        } catch (e) {
            return dateString;
        }
    };

    return (
        <CStack header="Recent">
            {isLoadingLibrary && (
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading history...</span>
                    </Spinner>
                </div>
            )}

            {error && !isLoadingLibrary && !isDeleting && (
                <Alert variant="danger">Error: {error}</Alert>
            )}

            {!isLoadingLibrary && libraryFiles.length > 0 && (
                <>
                    {libraryFiles.map((file: XMLLibraryEntry, index) => {
                        const fileKey = file.key || file.filename;
                        const generatedByText = file.generatedBy && (file.generatedBy.displayName || file.generatedBy.username)
                            ? `Generated by: ${file.generatedBy.displayName || file.generatedBy.username}`
                            : '';
                        
                        return (
                            <div
                                key={fileKey + index}
                                onMouseEnter={() => !isDeleting && setHoveredItemKey(fileKey)}
                                onMouseLeave={() => setHoveredItemKey(null)}
                                style={{ position: 'relative' }}
                            >
                                <BaseControl
                                    text={file.filename}
                                    caption={generatedByText}
                                    contentSize={6}
                                    enabled={!isLoadingFile && !isDeleting}
                                    onClick={() => handleFileClick(file.filename, file.description)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Label size="small" color="#6c757d" fontWeight="400">
                                            {formatDate(file.createdAt)}
                                        </Label>
                                        {!isLoadingFile && hoveredItemKey === fileKey && !isDeleting && (
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    onClick={(e) => handleDownload(e, file.filename)}
                                                    style={{
                                                        padding: '0.1rem 0.3rem',
                                                        lineHeight: '1',
                                                        color: '#0066cc'
                                                    }}
                                                    title={`Download ${file.filename}`}
                                                >
                                                    <Download size={18} />
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    onClick={(e) => handleDelete(e, fileKey, file.filename)}
                                                    disabled={isDeleting}
                                                    style={{
                                                        padding: '0.1rem 0.3rem',
                                                        lineHeight: '1',
                                                        color: 'red'
                                                    }}
                                                    title={`Delete ${file.filename}`}
                                                >
                                                    <XCircleFill size={18} />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </BaseControl>
                            </div>
                        );
                    })}
                </>
            )}

            {!isLoadingLibrary && !error && libraryFiles.length === 0 && (
                <p className="text-muted text-center">No generated files found in history.</p>
            )}

            {(isLoadingFile || isDeleting) && (
                <div className="text-center text-muted mt-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isLoadingFile ? 'Loading selected file...' : 'Deleting item...'}
                </div>
            )}
        </CStack>
    );
};

export default HistoryContainer;
